
import { useState, useEffect, useMemo } from 'react';
import { TimeSignature, IntegrityRules, SoundProfile } from '../../types';
import { APP_CONFIG } from '../../services/config';
import { normalizeHardwareLagMs } from '../../services/latencyUnits';

const getStorageItem = (key: string, defaultValue: any) => {
    if (typeof window === 'undefined') return defaultValue;
    const saved = localStorage.getItem(key);
    if (saved === null) return defaultValue;
    try { return JSON.parse(saved); } catch { return saved; }
};

export const SAFE_GAIN_MIN = 0.15;
export const SAFE_GAIN_DEFAULT = 1.0;

const DEFAULT_INTEGRITY_RULES: IntegrityRules = {
    enableSilenceGate: true,
    enableConfidenceGate: true,
    enableRhythmGuard: true,
    enableSpectralPurity: false,
    noiseFloorDb: -60,
    minConfidencePct: 65,
    allowSelfCheck: true,
    enableAntiReplay: false,
    rejectIdenticalFiles: false,
    rejectSuspiciousPerfectMatch: false,
    rejectStudentFileUpload: false,
    requireMediaRecorder: false
};

export function useAudioSettings() {
    // Persistent State
    const [lang, setLang] = useState<'en' | 'fr'>(() => getStorageItem('gsc_lang', APP_CONFIG.DEFAULT_LANG));
    const [targetBpm, setTargetBpm] = useState<number>(() => getStorageItem('gsc_target_bpm', APP_CONFIG.DEFAULT_BPM));
    const [manualGrid, setManualGrid] = useState<string>(() => getStorageItem('gsc_manual_grid', "G C | D | G"));
    const [hasDoneGlobalCalibration, setHasDoneGlobalCalibration] = useState<boolean>(() => getStorageItem('gsc_calib_done', false));
    
    // NORMALIZE ON LOAD
    const [globalCalibrationOffset, setGlobalCalibrationOffset] = useState<number>(() => 
        normalizeHardwareLagMs(getStorageItem('gsc_calib_offset', 0))
    );
    
    const [micGain, setMicGain] = useState<number>(() => Math.max(SAFE_GAIN_MIN, getStorageItem('gsc_mic_gain', SAFE_GAIN_DEFAULT)));
    const [integrityRules, setIntegrityRules] = useState<IntegrityRules>(() => getStorageItem('gsc_integrity_rules', DEFAULT_INTEGRITY_RULES));
    const [soundProfile, setSoundProfile] = useState<SoundProfile>(() => getStorageItem('gsc_sound_profile', 'CLEAN'));
    
    const [isGrooveEnabled, setIsGrooveEnabled] = useState<boolean>(false);
    const [isSafeAudioMode, setIsSafeAudioMode] = useState(false);

    // Ephemeral State
    const [atomicOffset, setAtomicOffset] = useState<number>(0);
    const [timeSignature, setTimeSignature] = useState<TimeSignature>('4/4');

    // Persistence Effects
    useEffect(() => { localStorage.setItem('gsc_lang', JSON.stringify(lang)); }, [lang]);
    useEffect(() => { localStorage.setItem('gsc_target_bpm', JSON.stringify(targetBpm)); }, [targetBpm]);
    useEffect(() => { localStorage.setItem('gsc_manual_grid', JSON.stringify(manualGrid)); }, [manualGrid]);
    useEffect(() => { localStorage.setItem('gsc_calib_done', JSON.stringify(hasDoneGlobalCalibration)); }, [hasDoneGlobalCalibration]);
    
    // NORMALIZE ON SAVE
    useEffect(() => { 
        const safeVal = normalizeHardwareLagMs(globalCalibrationOffset);
        localStorage.setItem('gsc_calib_offset', JSON.stringify(safeVal)); 
    }, [globalCalibrationOffset]);
    
    useEffect(() => { localStorage.setItem('gsc_mic_gain', JSON.stringify(micGain)); }, [micGain]);
    useEffect(() => { localStorage.setItem('gsc_integrity_rules', JSON.stringify(integrityRules)); }, [integrityRules]);
    useEffect(() => { localStorage.setItem('gsc_sound_profile', JSON.stringify(soundProfile)); }, [soundProfile]);

    return useMemo(() => ({
        lang, setLang,
        targetBpm, setTargetBpm,
        manualGrid, setManualGrid,
        hasDoneGlobalCalibration, setHasDoneGlobalCalibration,
        globalCalibrationOffset, setGlobalCalibrationOffset: (v: number) => setGlobalCalibrationOffset(normalizeHardwareLagMs(v)),
        micGain, setMicGain,
        integrityRules, setIntegrityRules,
        soundProfile, setSoundProfile,
        isGrooveEnabled, setIsGrooveEnabled,
        atomicOffset, setAtomicOffset,
        timeSignature, setTimeSignature,
        isSafeAudioMode, setIsSafeAudioMode
    }), [
        lang, targetBpm, manualGrid, hasDoneGlobalCalibration, globalCalibrationOffset,
        micGain, integrityRules, soundProfile, isGrooveEnabled, atomicOffset, timeSignature, isSafeAudioMode
    ]);
}
